import { z } from 'zod';

/**
 * The display preferences of the interface, set from the settings window,
 * remembered in `localStorage` for this browser and saved on the account by
 * `settings/sync.ts` (ADR 0029). Presentation only: none of them changes
 * what the server does; it stores them and never reads them.
 *
 * Sound has its own switches in `audio/sound.ts` (mute and the two volumes),
 * the keys theirs in `settings/keybindings.ts`.
 */

const STORAGE_KEY = 'shefira.preferences';

const PreferencesSchema = z
  .object({
    /** Damage numbers rising from a struck head. */
    damageNumbers: z.boolean(),
    /** The red frame around the screen while in combat. */
    combatFrame: z.boolean(),
    /** The minimap in the top-left corner. */
    minimap: z.boolean(),
    /** A short buzz when the player is struck, on a phone that can (ADR 0043). */
    vibration: z.boolean(),
    /** The round trip to the server, in bars and milliseconds, under the top buttons. */
    ping: z.boolean(),
    /** The screen's jolt on a critical hit, a heavy blow or a level (ADR 0049). */
    screenShake: z.boolean(),
    /** The red vignette and the heartbeat under a quarter of the health. */
    lowHealthAlert: z.boolean(),
    /** The chat panel; off, it shows only while the player types. */
    chat: z.boolean(),
    /** The night's darkening and the weather (ADR 0093); off, the world stays in full light and dry. */
    nightAndWeather: z.boolean(),
  })
  .partial();
export type Preferences = Required<z.infer<typeof PreferencesSchema>>;
export type PreferenceKey = keyof Preferences;

const DEFAULTS: Preferences = {
  damageNumbers: true,
  combatFrame: true,
  minimap: true,
  vibration: true,
  ping: false,
  screenShake: true,
  lowHealthAlert: true,
  chat: true,
  nightAndWeather: true,
};

const listeners = new Set<(key: PreferenceKey) => void>();
let preferences: Preferences = load();

function load(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULTS };
    const parsed = PreferencesSchema.safeParse(JSON.parse(raw));
    return parsed.success ? { ...DEFAULTS, ...parsed.data } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}

export function preference<K extends PreferenceKey>(key: K): Preferences[K] {
  return preferences[key];
}

export function setPreference<K extends PreferenceKey>(key: K, value: Preferences[K]): void {
  if (preferences[key] === value) return;
  preferences = { ...preferences, [key]: value };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    /* the preference still holds for this visit */
  }
  for (const listener of listeners) listener(key);
}

/** All the preferences at once, as the account saved them; the defaults fill the gaps. */
export function replacePreferences(saved: Partial<Preferences>): void {
  const next = { ...DEFAULTS, ...saved };
  const changed = (Object.keys(next) as PreferenceKey[]).filter(
    (key) => next[key] !== preferences[key],
  );
  preferences = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    /* the preferences still hold for this visit */
  }
  for (const key of changed) for (const listener of listeners) listener(key);
}

/** Every preference, for the account save. */
export function allPreferences(): Readonly<Preferences> {
  return preferences;
}

export function onPreferenceChange(listener: (key: PreferenceKey) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Keep an element shown or hidden after a boolean preference, now and at
 * every change. Returns the unsubscribe, for the element's `destroy`.
 */
export function followPreference(
  key:
    | 'damageNumbers'
    | 'combatFrame'
    | 'minimap'
    | 'ping'
    | 'lowHealthAlert'
    | 'chat'
    | 'nightAndWeather',
  apply: (enabled: boolean) => void,
): () => void {
  apply(preferences[key]);
  return onPreferenceChange((changed) => {
    if (changed === key) apply(preferences[key]);
  });
}
